import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  createFilterTemplate,
  deleteFilterTemplate,
  getFilterTemplate,
  listFilterTemplates,
  updateFilterTemplate,
} from "../services/filterTemplates";
import { invalidateUserProfile } from "../helpers/userProfile";

const FORBIDDEN_REDIRECT_DELAY_MS = 3000;
export const FILTER_TEMPLATE_TITLE_MAX_LENGTH = 48;

const toTemplateId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampTemplateTitle = (value) =>
  typeof value === "string"
    ? value.slice(0, FILTER_TEMPLATE_TITLE_MAX_LENGTH)
    : "";

const normalizeTemplateCollection = (templates) =>
  Array.isArray(templates) ? templates : [];

const normalizeTemplateFilters = (filters) =>
  filters && typeof filters === "object" && !Array.isArray(filters) ? filters : {};

const EMPTY_TEMPLATE_RANGE = {
  from: "",
  to: "",
  mode: "season",
};

const normalizeRangeMap = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getRangeParameterId = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const rawId = item.id ?? item.paramId ?? item.parameterId ?? item.parameter_id;
  const normalizedId = String(rawId ?? "").trim();

  return normalizedId || null;
};

// Accept both the current list shape (`[{ id, from, to, mode }]`) and the
// legacy object-keyed shape (`{ [id]: { from, to, mode } }`) so updates to
// templates saved by older builds keep working.
const normalizeRangeList = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === "object");
  }

  if (value && typeof value === "object") {
    return Object.entries(value).map(([id, range]) => ({
      id: Number(id),
      ...(range && typeof range === "object" ? range : {}),
    }));
  }

  return [];
};

const buildRangeMapWithRemovedKeysCleared = (currentRanges, previousRanges) => {
  const nextRanges = { ...normalizeRangeMap(currentRanges) };

  Object.keys(normalizeRangeMap(previousRanges)).forEach((parameterId) => {
    if (Object.prototype.hasOwnProperty.call(nextRanges, parameterId)) {
      return;
    }

    nextRanges[parameterId] = { ...EMPTY_TEMPLATE_RANGE };
  });

  return nextRanges;
};

const buildRangeListWithRemovedKeysCleared = (currentRanges, previousRanges) => {
  const nextRanges = normalizeRangeList(currentRanges);
  const presentIds = new Set(
    nextRanges.map(getRangeParameterId).filter(Boolean)
  );

  normalizeRangeList(previousRanges).forEach((item) => {
    const parameterId = getRangeParameterId(item);

    if (!parameterId || presentIds.has(parameterId)) {
      return;
    }

    presentIds.add(parameterId);
    nextRanges.push({ id: Number(parameterId), ...EMPTY_TEMPLATE_RANGE });
  });

  return nextRanges;
};

export const buildTemplateFiltersPayload = (filters, previousFilters) => {
  const nextFilters = normalizeTemplateFilters(filters);
  const previousTemplateFilters = normalizeTemplateFilters(previousFilters);

  if (!Object.keys(previousTemplateFilters).length) {
    return nextFilters;
  }

  const nextPayloadFilters = { ...nextFilters };

  ["advancedParamRanges", "parameterRanges"].forEach((rangeKey) => {
    const hasCurrentRanges = Object.prototype.hasOwnProperty.call(
      nextFilters,
      rangeKey
    );
    const hasPreviousRanges = Object.prototype.hasOwnProperty.call(
      previousTemplateFilters,
      rangeKey
    );

    if (!hasCurrentRanges && !hasPreviousRanges) {
      return;
    }

    nextPayloadFilters[rangeKey] =
      rangeKey === "parameterRanges"
        ? buildRangeListWithRemovedKeysCleared(
            nextFilters[rangeKey],
            previousTemplateFilters[rangeKey]
          )
        : buildRangeMapWithRemovedKeysCleared(
            nextFilters[rangeKey],
            previousTemplateFilters[rangeKey]
          );
  });

  return nextPayloadFilters;
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data?.message;

  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }

  return fallbackMessage;
};

const getTitleValidationMessage = (error, fallbackMessage) => {
  const titleErrors =
    error?.response?.data?.errors?.title ||
    error?.response?.data?.title;

  if (Array.isArray(titleErrors) && titleErrors.length) {
    return titleErrors.join(" ");
  }

  if (typeof titleErrors === "string" && titleErrors.trim()) {
    return titleErrors;
  }

  return getErrorMessage(error, fallbackMessage);
};

export const useSavedFilterTemplates = ({
  pageKey,
  filters,
  onApplyTemplate,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [saveMode, setSaveMode] = useState("create");
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  const [accessDenied, setAccessDenied] = useState(false);

  const isMountedRef = useRef(true);
  const forbiddenRedirectTimeoutRef = useRef(null);
  const translateRef = useRef(t);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  useEffect(
    () => () => {
      isMountedRef.current = false;

      if (forbiddenRedirectTimeoutRef.current) {
        window.clearTimeout(forbiddenRedirectTimeoutRef.current);
      }
    },
    []
  );

  const clearTemplateModals = useCallback(() => {
    setSaveModalOpen(false);
    setDeleteModalOpen(false);
    setPendingDeleteTemplate(null);
    setTemplateTitle("");
    setTitleError("");
    setSaveErrorMessage("");
  }, []);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("token");
    invalidateUserProfile();
    navigate("/login", { replace: true });
  }, [navigate]);

  const handleForbidden = useCallback(() => {
    setAccessDenied(true);

    if (forbiddenRedirectTimeoutRef.current) {
      window.clearTimeout(forbiddenRedirectTimeoutRef.current);
    }

    forbiddenRedirectTimeoutRef.current = window.setTimeout(() => {
      navigate("/dashboard");
    }, FORBIDDEN_REDIRECT_DELAY_MS);
  }, [navigate]);

  const refreshTemplates = useCallback(
    async ({ nextSelectedTemplateId } = {}) => {
      if (isMountedRef.current) {
        setIsLoadingTemplates(true);
        setTemplatesError("");
      }

      try {
        const nextTemplates = normalizeTemplateCollection(
          await listFilterTemplates(pageKey)
        );

        if (!isMountedRef.current) {
          return nextTemplates;
        }

        setTemplates(nextTemplates);
        setHasLoadedTemplates(true);

        setSelectedTemplateId((currentSelectedTemplateId) => {
          if (nextSelectedTemplateId === null) {
            return null;
          }

          if (nextSelectedTemplateId !== undefined) {
            return nextTemplates.some(
              (template) => toTemplateId(template?.id) === nextSelectedTemplateId
            )
              ? nextSelectedTemplateId
              : null;
          }

          return nextTemplates.some(
            (template) => toTemplateId(template?.id) === currentSelectedTemplateId
          )
            ? currentSelectedTemplateId
            : null;
        });

        return nextTemplates;
      } catch (error) {
        const status = error?.response?.status;

        if (status === 401) {
          handleUnauthorized();
          return [];
        }

        if (status === 403) {
          handleForbidden();
          return [];
        }

        if (isMountedRef.current) {
          setHasLoadedTemplates(true);
          setTemplates([]);
          setTemplatesError(
            getErrorMessage(
              error,
              translateRef.current("savedTemplates.loadError")
            )
          );
        }

        return [];
      } finally {
        if (isMountedRef.current) {
          setIsLoadingTemplates(false);
        }
      }
    },
    [handleForbidden, handleUnauthorized, pageKey]
  );

  useEffect(() => {
    setAccessDenied(false);
    setDropdownOpen(false);
    setSelectedTemplateId(null);
    setStatusMessage(null);
    clearTemplateModals();
    refreshTemplates({ nextSelectedTemplateId: null });
  }, [clearTemplateModals, pageKey, refreshTemplates]);

  const selectedTemplate = useMemo(
    () =>
      templates.find(
        (template) => toTemplateId(template?.id) === selectedTemplateId
      ) || null,
    [selectedTemplateId, templates]
  );

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen((previousDropdownOpen) => {
      const nextDropdownOpen = !previousDropdownOpen;

      if (
        nextDropdownOpen &&
        (!hasLoadedTemplates || templatesError)
      ) {
        refreshTemplates();
      }

      return nextDropdownOpen;
    });
  }, [hasLoadedTemplates, refreshTemplates, templatesError]);

  const handleOpenCreateModal = useCallback(() => {
    setSaveMode("create");
    setTemplateTitle("");
    setTitleError("");
    setSaveErrorMessage("");
    setSaveModalOpen(true);
  }, []);

  const handleOpenUpdateModal = useCallback(() => {
    if (!selectedTemplate) {
      return;
    }

    setSaveMode("update");
    setTemplateTitle(clampTemplateTitle(selectedTemplate?.title));
    setTitleError("");
    setSaveErrorMessage("");
    setSaveModalOpen(true);
  }, [selectedTemplate]);

  const handleCloseSaveModal = useCallback(() => {
    setSaveModalOpen(false);
    setTitleError("");
    setSaveErrorMessage("");
    setTemplateTitle("");
  }, []);

  const handleTemplateTitleChange = useCallback((value) => {
    setTemplateTitle(clampTemplateTitle(value));
    setTitleError("");
    setSaveErrorMessage("");
  }, []);

  const handleApplySelectedTemplate = useCallback(
    async (template) => {
      const nextTemplateId = toTemplateId(template?.id);

      if (!nextTemplateId) {
        return;
      }

      setSelectedTemplateId(nextTemplateId);
      setDropdownOpen(false);
      setStatusMessage(null);

      // The list endpoint can return a lighter row than the single-template
      // endpoint, so re-fetch the full template before applying it. This keeps
      // every saved filter (notably advanced parameter ranges) intact instead
      // of dropping whatever the list response omitted. Fall back to the row
      // already in hand if the detail request is unavailable.
      let appliedFilters = normalizeTemplateFilters(template?.filters);

      try {
        const fullTemplate = await getFilterTemplate(nextTemplateId);

        if (!isMountedRef.current) {
          return;
        }

        if (
          fullTemplate &&
          typeof fullTemplate === "object" &&
          fullTemplate.filters
        ) {
          appliedFilters = normalizeTemplateFilters(fullTemplate.filters);
        }
      } catch (error) {
        const status = error?.response?.status;

        if (status === 401) {
          handleUnauthorized();
          return;
        }

        if (status === 403) {
          handleForbidden();
          return;
        }

        if (!isMountedRef.current) {
          return;
        }
        // Any other failure falls back to the filters already on the row.
      }

      onApplyTemplate?.(appliedFilters);
    },
    [handleForbidden, handleUnauthorized, onApplyTemplate]
  );

  const handleSaveTemplate = useCallback(async () => {
    const normalizedTitle = templateTitle.trim();

    if (!normalizedTitle) {
      setTitleError(t("savedTemplates.titleRequired"));
      return;
    }

    if (saveMode === "update" && !selectedTemplate) {
      setStatusMessage({
        type: "error",
        text: t("savedTemplates.templateNotFound"),
      });
      setSaveModalOpen(false);
      return;
    }

    setIsSavingTemplate(true);
    setTitleError("");
    setSaveErrorMessage("");

    const payload = {
      title: normalizedTitle,
      page_key: pageKey,
      filters: buildTemplateFiltersPayload(
        filters,
        saveMode === "update" ? selectedTemplate?.filters : null
      ),
    };

    try {
      const savedTemplate =
        saveMode === "update"
          ? await updateFilterTemplate(selectedTemplate.id, payload)
          : await createFilterTemplate(payload);

      await refreshTemplates({
        nextSelectedTemplateId: toTemplateId(savedTemplate?.id),
      });

      if (!isMountedRef.current) {
        return;
      }

      onSaveSuccess?.(savedTemplate, saveMode);
      setStatusMessage(null);
      handleCloseSaveModal();
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401) {
        handleUnauthorized();
        return;
      }

      if (status === 403) {
        handleForbidden();
        return;
      }

      if (status === 404) {
        setSelectedTemplateId(null);
        setStatusMessage({
          type: "error",
          text: t("savedTemplates.templateNotFound"),
        });
        handleCloseSaveModal();
        await refreshTemplates({ nextSelectedTemplateId: null });
        return;
      }

      if (status === 422) {
        setTitleError(
          getTitleValidationMessage(
            error,
            t("savedTemplates.duplicateTitleError")
          )
        );
        return;
      }

      setSaveErrorMessage(
        getErrorMessage(
          error,
          saveMode === "update"
            ? t("savedTemplates.updateError")
            : t("savedTemplates.saveError")
        )
      );
    } finally {
      if (isMountedRef.current) {
        setIsSavingTemplate(false);
      }
    }
  }, [
    filters,
    handleCloseSaveModal,
    handleForbidden,
    handleUnauthorized,
    pageKey,
    refreshTemplates,
    saveMode,
    selectedTemplate,
    t,
    templateTitle,
    onSaveSuccess,
  ]);

  const handleOpenDeleteModal = useCallback((template) => {
    setPendingDeleteTemplate(template);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setPendingDeleteTemplate(null);
  }, []);

  const handleDeleteTemplate = useCallback(async () => {
    const templateId = toTemplateId(pendingDeleteTemplate?.id);

    if (!templateId) {
      return;
    }

    setIsDeletingTemplate(true);

    try {
      await deleteFilterTemplate(templateId);

      const shouldClearSelection = selectedTemplateId === templateId;

      await refreshTemplates({
        nextSelectedTemplateId: shouldClearSelection ? null : selectedTemplateId,
      });

      if (!isMountedRef.current) {
        return;
      }

      handleCloseDeleteModal();
      setStatusMessage(null);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401) {
        handleUnauthorized();
        return;
      }

      if (status === 403) {
        handleForbidden();
        return;
      }

      if (status === 404) {
        handleCloseDeleteModal();
        setSelectedTemplateId(null);
        setStatusMessage({
          type: "error",
          text: t("savedTemplates.templateNotFound"),
        });
        await refreshTemplates({ nextSelectedTemplateId: null });
        return;
      }

      setStatusMessage({
        type: "error",
        text: getErrorMessage(error, t("savedTemplates.deleteError")),
      });
    } finally {
      if (isMountedRef.current) {
        setIsDeletingTemplate(false);
      }
    }
  }, [
    handleCloseDeleteModal,
    handleForbidden,
    handleUnauthorized,
    pendingDeleteTemplate,
    refreshTemplates,
    selectedTemplateId,
    t,
  ]);

  const clearSelectedTemplateSelection = useCallback(() => {
    setSelectedTemplateId(null);
    setStatusMessage(null);
  }, []);

  return {
    accessDenied,
    deleteModalOpen,
    dropdownOpen,
    hasLoadedTemplates,
    isDeletingTemplate,
    isLoadingTemplates,
    isSavingTemplate,
    pendingDeleteTemplate,
    saveErrorMessage,
    saveModalOpen,
    saveMode,
    selectedTemplate,
    selectedTemplateId,
    statusMessage,
    templateTitle,
    templateTitleMaxLength: FILTER_TEMPLATE_TITLE_MAX_LENGTH,
    templates,
    templatesError,
    titleError,
    handleApplySelectedTemplate,
    handleCloseDeleteModal,
    handleCloseSaveModal,
    handleDeleteTemplate,
    handleDropdownToggle,
    handleOpenCreateModal,
    handleOpenDeleteModal,
    handleOpenUpdateModal,
    handleSaveTemplate,
    handleTemplateTitleChange,
    refreshTemplates,
    setDropdownOpen,
    setStatusMessage,
    clearSelectedTemplateSelection,
  };
};
